import React, { MouseEvent, useEffect, useState, useRef } from "react";
import Button from "../../../components/common/button";
import { connect } from "react-redux";
import ProductItem from "./productItem";
import { PRODUCT_TYPE, IProduct } from "../../../interfaces/products";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { History } from "history";
import { routes } from "../../routes";
import { Row, Col } from "reactstrap";
import { Box, ChevronLeft, ChevronRight } from "react-feather";
import PerfectScroll from "react-perfect-scrollbar";
import productService from "../../../services/productService";
import { IApplicationState } from "../../../redux/reducers";
import { IPaginateList } from "../../../interfaces/pagination";
import { Dispatch } from "redux";
import { paginateAction } from "../../../redux/actions/pagination/pagination";
import { product } from "../../../redux/actions/actionContants";
import Pagination from "../../../components/pagination";
import Loader from "../../../components/loader";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { IUser } from "../../../interfaces/user";
import Upgrade from "../../../components/common/upgrade";
import { IBrand } from "../../../interfaces/company";

interface IProps extends RouteComponentProps {
	history: History;
	getConceptProducts: () => Promise<any>;
	paginate: (page: number) => void;
	concept: IPaginateList<IProduct[]> & { cache: { [page: number]: IProduct[] } };
	removeProduct: (productId: string) => Promise<any>;
	profile?: IUser | undefined;
	selected_brand: IBrand;
}

const ConceptProducts: React.FC<IProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [removing, setRemoving] = useState<boolean>(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const {
		concept: { list, pagination }
	} = props;

	useEffect(() => {
		// Cancel any pending request
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Create new abort controller for this request
		abortControllerRef.current = new AbortController();

		setLoading(true);

		const currentPage = props.concept.pagination.page;
		if (props.concept.cache && props.concept.cache[currentPage]) {
			setLoading(false);
			return;
		}

		props.getConceptProducts()
			.finally(() => {
				setLoading(false);
				abortControllerRef.current = null;
			});

		// Cleanup function to abort request if component unmounts or dependencies change
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [pagination.page, props.selected_brand]);

	function onRemoveHandler(productId: string) {
		setRemoving(true);

		confirmAlert({
			title: "Remove Product",
			message: "Are you sure you want to remove this product?",
			buttons: [
				{
					label: "Remove",
					onClick: () => {
						props.removeProduct(productId).finally(() => {
							setLoading(true);
							props.getConceptProducts().finally(() => {
								setLoading(false);
							});
						});
					}
				},
				{
					label: "Cancel",
					onClick: () => { }
				}
			]
		});
	}

	const scrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: -300,
				behavior: 'smooth'
			});
		}
	};

	const scrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({
				left: 300,
				behavior: 'smooth'
			});
		}
	};

	return (
		<div className="concept-products">
			<span className="title mb-3">
				<h4 className="mr-2 font-weight-semibold">Concept Products</h4>
				<h4 className="color-bg-blue mr-3 font-weight-normal">
					{props.profile?.company?.subscription_type == "supplier" ? "-" : pagination.total}
				</h4>
				<Button color="warning" className="color-white">
					<Box size={18} className="mr-2" />
					Concept
				</Button>
			</span>
			{props.profile?.company?.subscription_type == "supplier" ? (
				<Upgrade />
			) : (
				<Loader type="spin" isLoading={loading}>
					<div className="concept-products-container">
						<button
							className="concept-scroll-btn concept-scroll-left"
							onClick={scrollLeft}
							disabled={loading || props.concept.list.length === 0}
						>
							<ChevronLeft size={20} />
						</button>
						<div
							className="concept-products-list"
							ref={scrollContainerRef}
						>
							{props.concept.list.map((product) => (
								<div key={product.id} className="concept-product-item">
									<ProductItem
										product={product}
										productType={PRODUCT_TYPE.CONCEPT}
										onRemoveHandler={onRemoveHandler}
									/>
								</div>
							))}
						</div>
						<button
							className="concept-scroll-btn concept-scroll-right"
							onClick={scrollRight}
							disabled={loading || props.concept.list.length === 0}
						>
							<ChevronRight size={20} />
						</button>
					</div>
				</Loader>
			)}
			{props.profile?.company?.subscription_type == "supplier" ? (
				<></>
			) : (
				<div className="pagination-wrapper pagination-right pagination-small">
					<Pagination
						className="pagination-right pagination-small"
						page={pagination.page}
						total={pagination.total}
						paginatorSize="md"
						pages={pagination.pages}
						size={pagination.size}
						handlePageChange={props.paginate}
						loading={loading}
					/>
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	concept: state.product.concept,
	profile: state.user.profile,
	selected_brand: state.user.selected_brand
});

const mapDispatchToProps = {
	getConceptProducts: () => productService.getConceptProducts(),
	paginate: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateAction(product.PAGINATE_CONCEPT_PRODUCT, page));
	},
	removeProduct: (productId: string) => productService.removeProduct(productId)
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ConceptProducts));
